/**
 * Verify Email Page
 * Handles email verification via token
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import AuthLayout from '../../components/auth/AuthLayout';
import Button from '../../components/auth/Button';
import Alert from '../../components/auth/Alert';
import { verifyEmail, sendVerificationEmail } from '../../services/authService';
import { ApiError } from '../../types/auth';

type VerificationStatus = 'verifying' | 'success' | 'error' | 'no-token';

const VerifyEmailPage: React.FC = () => {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const [status, setStatus] = useState<VerificationStatus>(
    token ? 'verifying' : 'no-token'
  );
  const [error, setError] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  // Prevent double execution in React StrictMode
  const hasVerified = useRef(false);

  // Verify email on mount if token exists
  const performVerification = useCallback(async () => {
    if (!token) {
      setStatus('no-token');
      return;
    }

    // Prevent duplicate calls (React StrictMode in dev mode)
    if (hasVerified.current) {
      return;
    }
    hasVerified.current = true;

    try {
      await verifyEmail(token);
      setStatus('success');
    } catch (err) {
      const apiError = err as ApiError;
      setStatus('error');
      if (apiError.statusCode === 400 || apiError.statusCode === 401) {
        setError(t('auth:verifyEmailPage.failedDescription'));
      } else {
        setError(apiError.message || t('auth:verifyEmail.failed'));
      }
    }
  }, [token, t]);

  useEffect(() => {
    performVerification();
  }, [performVerification]);

  // Handle resend verification email
  const handleResend = async () => {
    setResendLoading(true);
    setResendSuccess(false);

    try {
      await sendVerificationEmail();
      setResendSuccess(true);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || t('auth:profile.verificationFailed'));
    } finally {
      setResendLoading(false);
    }
  };

  // Verifying state
  if (status === 'verifying') {
    return (
      <AuthLayout
        title={t('auth:verifyEmailPage.verifying')}
        subtitle={t('auth:verifyEmailPage.verifyingDescription')}
      >
        <div className="text-center">
          <div className="mb-6">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <svg
                className="animate-spin h-12 w-12 text-primary-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
            <p className="text-gray-600">
              {t('auth:verifyEmailPage.verifyingDescription')}
            </p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <AuthLayout
        title={t('auth:verifyEmailPage.success')}
        subtitle={t('auth:verifyEmailPage.successDescription')}
      >
        <div className="text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t('auth:verifyEmailPage.success')}
            </h3>
            <p className="text-gray-600 mb-4">
              {t('auth:verifyEmailPage.successDescription')}
            </p>
          </div>

          <Link to="/login">
            <Button fullWidth>
              {t('auth:verifyEmailPage.goToLogin')}
            </Button>
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // Error or no-token state
  return (
    <AuthLayout
      title={status === 'no-token' ? t('auth:verifyEmail.title') : t('auth:verifyEmailPage.failed')}
      subtitle={
        status === 'no-token'
          ? t('auth:verifyEmail.subtitle')
          : t('auth:verifyEmailPage.failedDescription')
      }
    >
      <div className="text-center">
        {error && (
          <Alert type="error" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        {resendSuccess && (
          <Alert type="success" onDismiss={() => setResendSuccess(false)}>
            {t('auth:emailVerificationBanner.verificationSent')}
          </Alert>
        )}

        <div className="mb-6">
          <div
            className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
              status === 'no-token' ? 'bg-blue-100' : 'bg-red-100'
            }`}
          >
            {status === 'no-token' ? (
              <svg
                className="w-8 h-8 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            ) : (
              <svg
                className="w-8 h-8 text-red-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            )}
          </div>

          {status === 'no-token' ? (
            <>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('auth:register.checkEmail')}
              </h3>
              <p className="text-gray-600 mb-4">
                {t('auth:register.clickToVerify')}
              </p>
              <p className="text-sm text-gray-500 mb-6">
                {t('auth:register.didntReceive')}
              </p>
            </>
          ) : (
            <>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {t('auth:verifyEmailPage.failed')}
              </h3>
              <p className="text-gray-600 mb-4">
                {t('auth:verifyEmailPage.failedDescription')}
              </p>
            </>
          )}
        </div>

        <div className="space-y-3">
          <Button
            onClick={handleResend}
            isLoading={resendLoading}
            disabled={resendLoading}
            fullWidth
            variant={status === 'error' ? 'primary' : 'outline'}
          >
            {t('auth:verifyEmailPage.resend')}
          </Button>

          <Link to="/login">
            <Button fullWidth variant="outline">
              {t('auth:verifyEmailPage.goToLogin')}
            </Button>
          </Link>
        </div>
      </div>
    </AuthLayout>
  );
};

export default VerifyEmailPage;
